import { getSiteId, useAppSelector } from '@la/services';
import { getLAHostnameParts } from '@la/utilities';
import { useGetSiteProgramsQuery } from 'redux/services/siteLevelCalendarApi';
import { Program } from 'domains/ActivityForm/openapi-client/models';

export const PROGRAM_COLORS = [
  { name: 'purple-500', color: '#6431F6' },
  { name: 'teal-500', color: '#04C6D2' },
  { name: 'lime-500', color: '#CBE332' },
  { name: 'peach-500', color: '#FF7A51' },
  { name: 'fuchsia-500', color: '#FF71B5' },

  { name: 'purple-700', color: '#4622AC' },
  { name: 'teal-700', color: '#038B93' },
  { name: 'lime-700', color: '#8E9F23' },
  { name: 'peach-700', color: '#B35539' },
  { name: 'fuchsia-700', color: '#B34F7F' },

  { name: 'purple-300', color: '#936FF9' },
  { name: 'teal-300', color: '#4FD7E0' },
  { name: 'lime-300', color: '#DBEB70' },
  { name: 'peach-300', color: '#FFA285' },
  { name: 'fuchsia-300', color: '#FF9CCB' },

  { name: 'purple-100', color: '#D1C1FC' },
  { name: 'teal-100', color: '#B4EEF2' },
  { name: 'lime-100', color: '#EFF7C2' },
  { name: 'peach-100', color: '#FFD7CB' },
  { name: 'fuchsia-100', color: '#FFD4E9' },

  { name: 'purple-800', color: '#32197B' },
  { name: 'teal-800', color: '#026369' },
  { name: 'lime-800', color: '#667219' },
  { name: 'peach-800', color: '#803D29' },
  { name: 'fuchsia-800', color: '#80395B' },

  { name: 'purple-400', color: '#7446F7' },
  { name: 'teal-400', color: '#1DCCD7' },
  { name: 'lime-400', color: '#D0E647' },
  { name: 'peach-400', color: '#FF8762' },
  { name: 'fuchsia-400', color: '#FF7FBC' },

  { name: 'purple-600', color: '#5A2CDD' },
  { name: 'teal-600', color: '#04B2BD' },
  { name: 'lime-600', color: '#B7CC2D' },
  { name: 'peach-600', color: '#E66E49' },
  { name: 'fuchsia-600', color: '#E666A3' },

  { name: 'blue-500', color: '#3679FA' },
  { name: 'aqua-500', color: '#16DBAC' },
  { name: 'lightOrange-500', color: '#FFB341' },
  { name: 'pink-500', color: '#FF6883' },
  { name: 'violet-500', color: '#E357DE' },

  { name: 'blue-700', color: '#2655AF' },
  { name: 'aqua-700', color: '#0F9978' },
  { name: 'lightOrange-700', color: '#B37D2E' },
  { name: 'pink-700', color: '#B33D52' },
  { name: 'violet-700', color: '#9F3D9B' },

  { name: 'blue-300', color: '#72A1FC' },
  { name: 'aqua-300', color: '#5CE6C5' },
  { name: 'lightOrange-300', color: '#FFCA7A' },
  { name: 'pink-300', color: '#FF899E' },
  { name: 'violet-300', color: '#EB89E8' },

  { name: 'blue-100', color: '#C3D7FE' },
  { name: 'aqua-100', color: '#B9F4E6' },
  { name: 'lightOrange-100', color: '#FFE8C6' },
  { name: 'pink-100', color: '#FFCDD6' },
  { name: 'violet-100', color: '#F7CDF5' },

  { name: 'blue-800', color: '#204996' },
  { name: 'aqua-800', color: '#0B6E56' },
  { name: 'lightOrange-800', color: '#805A21' },
  { name: 'pink-800', color: '#802C3B' },
  { name: 'violet-800', color: '#722C6F' },

  { name: 'blue-400', color: '#4A86FB' },
  { name: 'aqua-400', color: '#2DDFB4' },
  { name: 'lightOrange-400', color: '#FFBB54' },
  { name: 'pink-400', color: '#FF6883' },
  { name: 'violet-400', color: '#E668E1' },

  { name: 'blue-600', color: '#316DE1' },
  { name: 'aqua-600', color: '#14C59B' },
  { name: 'lightOrange-600', color: '#E6A13B' },
  { name: 'pink-600', color: '#E64E69' },
  { name: 'violet-600', color: '#CC4EC8' },
];

export function useColorClassName(programId: number) {
  const siteId = useAppSelector(getSiteId);
  const { subdomain } = getLAHostnameParts();

  const { data: programs = [] } = useGetSiteProgramsQuery({
    siteId: siteId?.toString() || '0',
    subdomain,
  });

  const programsWithColor = getProgramsWithColors(programs);
  const program = programsWithColor.find((program) => program.id === programId);

  return {
    colorName: program?.colorName || 'default',
    colorHex: program?.colorHex || '#cd5c5c',
  };
}

function getProgramsWithColors(programs: Program[]) {
  if (!programs) {
    return [];
  }

  return programs.map((program, index) => {
    const programColor = PROGRAM_COLORS[index % PROGRAM_COLORS.length];
    return {
      id: program.id,
      name: program.name,
      colorName: programColor.name,
      colorHex: programColor.color,
    };
  });
}
